import React, { useState } from 'react';

const PhoneTab = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validatePhoneNumber = (phone) => {
    // Validates format: +1XXXXXXXXXX where X is a digit
    const phoneRegex = /^\+1\d{10}$/;
    return phoneRegex.test(phone);
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters except +
    let cleaned = value.replace(/[^\d+]/g, '');
    
    // If it starts with +1, keep it, otherwise add +1 if user starts typing digits
    if (cleaned.startsWith('+1')) {
      // Limit to +1 + 10 digits
      if (cleaned.length > 12) {
        cleaned = cleaned.substring(0, 12);
      }
    } else if (cleaned.startsWith('1') && cleaned.length > 1) {
      cleaned = '+' + cleaned;
      if (cleaned.length > 12) {
        cleaned = cleaned.substring(0, 12);
      }
    } else if (cleaned.length > 0 && !cleaned.startsWith('+')) {
      cleaned = '+1' + cleaned;
      if (cleaned.length > 12) {
        cleaned = cleaned.substring(0, 12);
      }
    }
    
    return cleaned;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const formattedValue = formatPhoneNumber(value);
    setPhoneNumber(formattedValue);
    setError('');
    setSuccess('');
  };

  const handleCall = async () => {
    setError('');
    setSuccess('');

    if (!phoneNumber.trim()) {
      setError('Phone number is required');
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError('Please enter a valid phone number in format: +1XXXXXXXXXX');
      return;
    }

    setIsLoading(true);

    try {
      // Make API call through AWS API Gateway proxy to avoid CORS issues
      const response = await fetch('https://fvhb1r51k5.execute-api.us-west-2.amazonaws.com/production/make-call', {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone_number: phoneNumber
        })
      });

      if (response.ok) {
        // Call was successful
        setSuccess(`Call initiated successfully to ${phoneNumber}`);
        setPhoneNumber('');
      } else {
        // Handle HTTP error responses
        let errorMessage = `Failed to initiate call: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.text();
          if (errorData) {
            errorMessage += ` - ${errorData}`;
          }
        } catch (e) {
          // If we can't read the error response, just use the status
        }
        setError(errorMessage);
      }
    } catch (error) {
      // Handle network errors, CORS issues, etc.
      let errorMessage = `Error making call: ${error.message}`;
      if (error.message === 'Failed to fetch') {
        errorMessage = 'Unable to connect to the call service. This might be due to network issues or CORS restrictions.';
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleCall();
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: '16px', color: '#333' }}>Make Phone Call</h2>
      <p className="tab-description">
        Enter a phone number to initiate a call. The number must be in US format (+1XXXXXXXXXX).
      </p>
      
      <div className="input-group">
        <input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          onKeyPress={handleKeyPress}
          placeholder="Enter phone number (+1XXXXXXXXXX)"
          className={`input-field ${error ? 'error' : ''}`}
          disabled={isLoading}
          maxLength={12}
        />
        <button
          onClick={handleCall}
          disabled={isLoading}
          className="action-btn"
        >
          {isLoading ? 'Calling...' : 'Call'}
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <h4 style={{ marginBottom: '8px', color: '#495057' }}>Phone Number Format Requirements:</h4>
        <ul style={{ color: '#6c757d', fontSize: '14px', paddingLeft: '20px' }}>
          <li>Must start with +1 (US country code)</li>
          <li>Must be followed by exactly 10 digits</li>
          <li>Total format: +1XXXXXXXXXX</li>
          <li>Examples: +15551234567, +19876543210</li>
        </ul>
        <div style={{ marginTop: '12px', fontSize: '14px', color: '#495057' }}>
          <strong>Tip:</strong> Start typing digits and the +1 will be added automatically!
        </div>
      </div>
    </div>
  );
};

export default PhoneTab;
